import { useRef, useState, useEffect, useMemo } from 'react';
import WaveSurfer, { type WaveSurferOptions, type WaveSurferEvents } from 'wavesurfer.js';

export interface WaveSurferPlayerProps {
  url: string;
  waveColor?: string | string[];
  progressColor?: string;
  barWidth?: number;
  barGap?: number;
  barRadius?: number;
  barHeight?: number;
  containerStyles?: React.CSSProperties;
  height?: number;
  width?: string | number;
  playPauseButtonSx?: React.CSSProperties;
  containerSx?: React.CSSProperties;
  wavefHeight?: string | number;
  cursorWidth?: number;
}

type WavesurferEventHandler<T extends unknown[]> = (wavesurfer: WaveSurfer, ...args: T) => void;

type OnWavesurferEvents = {
  [K in keyof WaveSurferEvents as `on${Capitalize<K>}`]?: WavesurferEventHandler<WaveSurferEvents[K]>;
};

type PartialWavesurferOptions = Omit<WaveSurferOptions, 'container'>;

export type WavesurferProps = PartialWavesurferOptions & OnWavesurferEvents & WaveSurferPlayerProps;

function useWavesurferInstance(containerRef: React.RefObject<HTMLElement>, options: Partial<WaveSurferOptions>): WaveSurfer | null {
  const [wavesurfer, setWavesurfer] = useState<WaveSurfer | null>(null);
  const flatOptions = useMemo(() => Object.entries(options).flat(), [options]);

  useEffect(() => {
    if (!containerRef.current) return;

    const ws = WaveSurfer.create({
      ...options,
      container: containerRef.current,
    });

    setWavesurfer(ws);

    return () => {
      ws.destroy();
    };
  }, [containerRef, ...flatOptions]);

  return wavesurfer;
}

function useWavesurferState(wavesurfer: WaveSurfer | null) {
  const [isReady, setIsReady] = useState<boolean>(false);
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [currentTime, setCurrentTime] = useState<number>(0);

  useEffect(() => {
    if (!wavesurfer) return;

    const handlers: Partial<{ [K in keyof WaveSurferEvents]: () => void }> = {
      load: () => {
        setIsReady(false);
        setIsPlaying(false);
        setCurrentTime(0);
      },
      ready: () => {
        setIsReady(true);
        setIsPlaying(false);
        setCurrentTime(0);
      },
      play: () => setIsPlaying(true),
      pause: () => setIsPlaying(false),
      timeupdate: () => setCurrentTime(wavesurfer.getCurrentTime()),
      destroy: () => {
        setIsReady(false);
        setIsPlaying(false);
      },
    };

    Object.entries(handlers).forEach(([event, handler]) => {
      wavesurfer.on(event as keyof WaveSurferEvents, handler!);
    });

    return () => {
      Object.entries(handlers).forEach(([event, handler]) => {
        wavesurfer.un(event as keyof WaveSurferEvents, handler!);
      });
    };
  }, [wavesurfer]);

  return { isReady, isPlaying, currentTime };
}

const EVENT_PROP_RE = /^on([A-Z])/;
const isEventProp = (key: string) => EVENT_PROP_RE.test(key);
const getEventName = (key: string) => key.replace(EVENT_PROP_RE, (_, $1) => $1.toLowerCase()) as keyof WaveSurferEvents;

function useWavesurferProps(props: any): [Partial<WaveSurferOptions>, OnWavesurferEvents] {
  return useMemo(() => {
    const allOptions = { ...props };
    const allEvents = { ...props };

    for (const key in allOptions) {
      if (isEventProp(key)) {
        delete allOptions[key as keyof typeof allOptions];
      } else {
        delete allEvents[key as keyof typeof allEvents];
      }
    }
    return [allOptions, allEvents];
  }, [props]);
}

function useWavesurferEvents(wavesurfer: WaveSurfer | null, events: OnWavesurferEvents) {
  const flatEvents = useMemo(() => Object.entries(events).flat(), [events]);

  useEffect(() => {
    if (!wavesurfer) return;

    const eventEntries = Object.entries(events);
    if (!eventEntries.length) return;

    const unsubscribeFns = eventEntries.map(([name, handler]) => {
      const event = getEventName(name);
      return wavesurfer.on(event, (...args) => (handler as WavesurferEventHandler<WaveSurferEvents[typeof event]>)(wavesurfer, ...args));
    });

    return () => {
      unsubscribeFns.forEach((fn) => fn());
    };
  }, [wavesurfer, ...flatEvents]);
}

export function useWaveSurfer(props: WavesurferProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [options, events] = useWavesurferProps(props);
  const wavesurfer = useWavesurferInstance(containerRef, options);
  useWavesurferEvents(wavesurfer, events);
  const { isReady, isPlaying, currentTime } = useWavesurferState(wavesurfer);

  const togglePlayPause = () => {
    if (wavesurfer) {
      wavesurfer.playPause();
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };

  return {
    containerRef,
    wavesurfer,
    isReady,
    isPlaying,
    currentTime,
    togglePlayPause,
    formatTime,
  };
}
